/**
 * Per-instance event channel for the search demo (item #8838).
 *
 * Autoplay used to type by broadcasting `vo-search-type-input` on `window`,
 * and every `demoMode` SearchInput on the page listened for it. That made a
 * second live demo impossible rather than merely awkward: two mounted demos
 * receive each other's keystrokes, garble both inputs, and each fires its own
 * query -- four demos would divide the #4968 rate budget by four while showing
 * one visitor one demo.
 *
 * A transport belongs to one DemoSearch. It is created for it -- by the embed,
 * which is the only place that knows which container the demo lives in, or by
 * DemoSearch itself when nobody supplies one -- passed down as a prop, and read
 * by SearchInput from props, so the delivery path carries the identity of the
 * widget it is driving.
 *
 * What stays on `window` is what is genuinely page-wide: the server's
 * rate-limit back-pressure, which throttles a visitor rather than a widget. The
 * embed's halt was classified that way too and was not (item #9013) -- a demo
 * swapped to its screenshot is one panel failing, and broadcasting it stopped
 * the demo the visitor was looking at.
 */

/** Autoplay typed a progressive partial. Detail: `{ text }`. */
export const DEMO_TYPE_INPUT = 'type-input';

/** Autoplay (or a user takeover) cleared the input. No detail. */
export const DEMO_CLEAR_INPUT = 'clear-input';

/**
 * This demo has been swapped to its screenshot and must stop for good (item
 * #9013). No detail.
 *
 * Permanent, like a user takeover: the widget behind it is hidden, so there is
 * nothing left to type into. Named per instance precisely because it IS
 * permanent -- a page-wide send would end every demo on the page.
 */
export const DEMO_HALT = 'halt';

/**
 * @typedef {Object} DemoTransport
 * @property {function(string, function(Object=): void): function(): void} on -
 *   Subscribe to an event; returns the unsubscribe function.
 * @property {function(string, Object=): void} emit - Deliver an event to this
 *   transport's subscribers, and to nothing else on the page.
 * @property {function(string): boolean} hasSubscriber - Whether anything is
 *   currently listening for an event. A mounted SearchInput is a subscriber,
 *   which is how autoplay knows a surface exists to type into (#8842).
 * @property {function(function(string): void): function(): void}
 *   onSubscriberChange - Notified when an event gains its first subscriber or
 *   loses its last; returns the unsubscribe function.
 */

/**
 * Build a demo transport.
 *
 * @returns {DemoTransport}
 *
 * @example
 * const transport = createDemoTransport();
 * const off = transport.on(DEMO_TYPE_INPUT, ({ text }) => setQuery(text));
 * transport.emit(DEMO_TYPE_INPUT, { text: 'instant search' });
 * off();
 */
export function createDemoTransport() {
  const subscribers = new Map();
  const watchers = new Set();

  /** Announce that `type` gained its first subscriber or lost its last. */
  function announce(type) {
    for (const watcher of [...watchers]) watcher(type);
  }

  return {
    on(type, handler) {
      if (!subscribers.has(type)) subscribers.set(type, new Set());
      const handlers = subscribers.get(type);
      handlers.add(handler);
      if (handlers.size === 1) announce(type);

      return () => {
        if (!handlers.delete(handler)) return;
        if (handlers.size === 0) announce(type);
      };
    },

    emit(type, detail) {
      const handlers = subscribers.get(type);
      if (!handlers) return;
      // Copy first: a handler that unsubscribes during delivery must not
      // change the set being iterated.
      for (const handler of [...handlers]) handler(detail);
    },

    hasSubscriber(type) {
      const handlers = subscribers.get(type);
      return !!handlers && handlers.size > 0;
    },

    onSubscriberChange(watcher) {
      watchers.add(watcher);
      return () => watchers.delete(watcher);
    },
  };
}
